import React from "react";
import {
  CompassOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { ProLayout } from "@ant-design/pro-components";
import { Button, Segmented, Space, Tooltip } from "antd";
import { Link, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import keycloak from "./keycloak";
import { WorkflowWizard } from "./workflow/WorkflowWizard";
import { DdsRoutes } from "./compliance/DdsRoutes";
import { SupplierPublicPage } from "./compliance/SupplierPublicPage";
import { CollaboratorPublicPage } from "./compliance/CollaboratorPublicPage";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { UiDensityContext } from "./components/AntdLocaleProvider";
import { CommandPalette, useCommandPaletteHotkey } from "./compliance/components/CommandPalette";
import { NotificationCenter } from "./compliance/components/NotificationCenter";
import { OnboardingTourTrigger } from "./compliance/components/OnboardingTour";
import { RequireAuth } from "./auth/RequireAuth";
import { useAuthStatus } from "./auth/AuthStatus";
import { WelcomePage } from "./investor/WelcomePage";
import { PublicDossierPage } from "./investor/PublicDossierPage";
import { TrustStrip } from "./components/TrustStrip";
import { useNarrowViewport } from "./compliance/ux/useNarrowViewport";

type NavKey = "workflow" | "dds";

/**
 * US-800 / US-806: show public landing at `/` immediately.
 * Redirect to `/dds` only after Keycloak check-sso reports an authenticated session.
 * Unauthenticated (or SSO still pending) keeps the same content as `/welcome`.
 */
export function RootRedirect() {
  const { authReady } = useAuthStatus();
  if (authReady && keycloak.authenticated) {
    return <Navigate to="/dds" replace />;
  }
  return <WelcomePage />;
}

function MainLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedNav, setSelectedNav] = React.useState<NavKey>("dds");
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const { density, setDensity } = React.useContext(UiDensityContext);
  const isNarrow = useNarrowViewport("md");

  useCommandPaletteHotkey(setPaletteOpen);

  const navItems: { key: NavKey; label: string; icon: React.ReactNode; path: string }[] = [
    { key: "workflow", label: t("app.nav.workflow"), icon: <CompassOutlined />, path: "/workflow" },
    { key: "dds", label: t("app.nav.dds"), icon: <SafetyCertificateOutlined />, path: "/dds" },
  ];

  React.useEffect(() => {
    if (location.pathname.startsWith("/workflow")) {
      setSelectedNav("workflow");
    } else {
      setSelectedNav("dds");
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await keycloak.logout({
      redirectUri: window.location.origin + "/welcome",
    });
  };

  const goHome = () => {
    setSelectedNav("dds");
    navigate("/dds");
  };

  return (
    <>
      <ProLayout
        title={false}
        logo={
          <span
            role="link"
            tabIndex={0}
            aria-label={t("app.logoAria")}
            onClick={goHome}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                goHome();
              }
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            <SafetyCertificateOutlined style={{ fontSize: 20 }} aria-hidden />
            <span style={{ fontWeight: 700, letterSpacing: 0.3, color: "#000" }}>{t("app.brand")}</span>
          </span>
        }
        onMenuHeaderClick={goHome}
        layout="mix"
        fixSiderbar
        breakpoint="lg"
        route={{
          routes: navItems.map((item) => ({
            path: item.path,
            name: item.label,
            icon: item.icon,
          })),
        }}
        location={{
          pathname: selectedNav === "dds" ? "/dds" : "/workflow",
        }}
        menuItemRender={(item, dom) => (
          <a
            href={item.path}
            onClick={(event) => {
              event.preventDefault();
              const match = navItems.find((n) => n.path === item.path);
              if (match) {
                setSelectedNav(match.key);
                navigate(match.path);
              }
            }}
          >
            {dom}
          </a>
        )}
        actionsRender={() => [
          <Space
            key="chrome-actions"
            size={isNarrow ? "small" : "middle"}
            wrap
            className="dds-chrome-actions"
            data-testid="dds-chrome-actions"
            data-narrow={isNarrow ? "true" : "false"}
          >
            {!isNarrow && (
              <Link to="/welcome" data-testid="nav-welcome" style={{ color: "inherit" }}>
                {t("investor.welcome.navLink")}
              </Link>
            )}
            <Tooltip title={t("compliance.commandPalette.hotkeyHint")}>
              <Button
                type="text"
                className="dds-touch-target"
                icon={<SearchOutlined />}
                data-tour="dds-command-palette"
                data-testid="command-palette-trigger"
                aria-label={t("compliance.commandPalette.title")}
                onClick={() => setPaletteOpen(true)}
              />
            </Tooltip>
            {!isNarrow && (
              <button
                type="button"
                className="dds-hotkey-chip"
                data-testid="command-palette-hotkey-chip"
                aria-label={t("compliance.commandPalette.hotkeyHint")}
                onClick={() => setPaletteOpen(true)}
              >
                <kbd>
                  {typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
                    ? t("compliance.commandPalette.hotkeyChip")
                    : t("compliance.commandPalette.hotkeyChipCtrl")}
                </kbd>
              </button>
            )}
            <NotificationCenter />
            <Tooltip title={t("compliance.notificationPrefs.title")}>
              <Button
                type="text"
                className="dds-touch-target"
                icon={<SettingOutlined />}
                data-testid="notification-settings-link"
                aria-label={t("compliance.notificationPrefs.title")}
                onClick={() => navigate("/dds/settings")}
              />
            </Tooltip>
            {!isNarrow && <OnboardingTourTrigger />}
            {!isNarrow && (
              <Segmented
                size="small"
                value={density}
                onChange={(v) => setDensity(v as "default" | "compact")}
                options={[
                  { label: t("compliance.density.default"), value: "default" },
                  { label: t("compliance.density.compact"), value: "compact" },
                ]}
                data-testid="ui-density-toggle"
              />
            )}
            <LanguageSwitcher />
            <Button key="logout" className="dds-touch-target" onClick={handleLogout}>
              {t("app.logout")}
            </Button>
          </Space>,
        ]}
        contentStyle={{ padding: isNarrow ? 12 : 24 }}
      >
        <Outlet />
        <footer className="app-footer" data-testid="app-footer">
          <TrustStrip />
        </footer>
      </ProLayout>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/share/:token" element={<PublicDossierPage />} />
      <Route path="/supplier/:token" element={<SupplierPublicPage />} />
      <Route path="/collaborator/:token" element={<CollaboratorPublicPage />} />
      <Route
        element={
          <RequireAuth>
            <MainLayout />
          </RequireAuth>
        }
      >
        <Route path="workflow/*" element={<WorkflowWizard />} />
        <Route path="dds/*" element={<DdsRoutes />} />
        <Route path="*" element={<Navigate to="/dds" replace />} />
      </Route>
    </Routes>
  );
}
